import { useState, type ReactNode } from 'react';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { Box, Collapse, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import { ApplicationSectionIcon } from '../application/applicationSectionChrome';

export function VerificationEvidence({
  children,
  expanded: controlledExpanded,
  defaultExpanded = false,
  onExpandedChange,
  id = 'employer-verification-details',
  title = 'View verification details',
  subtitle = 'One issue at a time — each brief includes case summary, review measures, and follow-up evidence.',
  variant = 'verification',
}: {
  children: ReactNode;
  expanded?: boolean;
  defaultExpanded?: boolean;
  onExpandedChange?: (open: boolean) => void;
  id?: string;
  title?: string;
  subtitle?: string;
  variant?: 'verification' | 'gate-analysis';
}) {
  const [internalOpen, setInternalOpen] = useState(defaultExpanded);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const open = controlledExpanded ?? internalOpen;

  const setOpen = (next: boolean) => {
    if (controlledExpanded === undefined) setInternalOpen(next);
    onExpandedChange?.(next);
  };

  return (
    <Box
      id={id}
      data-tour-target={id}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{
          px: 1.75,
          py: 1.25,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: 'action.hover' },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: -2,
          },
        }}
        onClick={() => setOpen(!open)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(!open);
          }
        }}
      >
        <ApplicationSectionIcon color={variant === 'gate-analysis' ? 'secondary' : 'info'}>
          {variant === 'gate-analysis' ? (
            <TableChartOutlinedIcon fontSize="small" />
          ) : (
            <FactCheckRoundedIcon fontSize="small" />
          )}
        </ApplicationSectionIcon>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
            {subtitle}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label={open ? 'Collapse verification details' : 'Expand verification details'}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
          }}
          sx={{ flexShrink: 0 }}
        >
          <ExpandMoreRoundedIcon
            sx={{
              transform: reduceMotion ? 'none' : open ? 'rotate(180deg)' : 'none',
              transition: reduceMotion ? 'none' : 'transform 0.2s ease',
            }}
          />
        </IconButton>
      </Stack>
      <Collapse in={open} timeout={reduceMotion ? 0 : 'auto'}>
        <Box sx={{ px: 1.75, pb: 1.75, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

export function GateAnalysisSection({
  title = 'Gate analysis',
  subtitle,
  endAdornment,
  children,
}: {
  title?: string;
  subtitle?: string;
  endAdornment?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box data-testid="employer-gate-analysis-panel" data-tour-target="employer-gate-analysis">
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.25 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2, lineHeight: 1.35 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {endAdornment ? (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
            {endAdornment}
          </Stack>
        ) : null}
      </Stack>
      {children}
    </Box>
  );
}
